/**
 * Adapated from https://github.com/ZeeCoder/use-resize-observer
 *
 * The MIT License (MIT)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
*/

import {useState, useLayoutEffect} from 'react';

// `ResizeObserver` does not have wide browser support at the moment, hence this polyfill.
import ResizeObserver from 'resize-observer-polyfill';

const useResizeObserver = (ref, {defaultWidth = 0, defaultHeight = 0} = {}) => {
  const [width, setWidth] = useState(defaultWidth);
  const [height, setHeight] = useState(defaultHeight);
  useLayoutEffect(() => {
    const element = ref.current;
    // In cases where there is no element (yet), don't do anything.
    if (!element) return;
    const {width: initialWidth, height: initialHeight} = element.getBoundingClientRect().toJSON();
    setWidth(initialWidth);
    setHeight(initialHeight);
    const resizeObserver = new ResizeObserver((entries) => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      // `ResizeObserver` can observe many elements, but in this case we know we only ever have one.
      const onlyEntry = entries[0];
      setWidth(onlyEntry.contentRect.width);
      setHeight(onlyEntry.contentRect.height);
    });
    resizeObserver.observe(element);
    return () => resizeObserver.unobserve(element);
  }, [ref.current]);
  return [width, height];
};

export default useResizeObserver;
